import React from 'react';
import { Helmet } from 'react-helmet';
import LegalNav from '../components/LegalNav';

const ContentPolicy = () => {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <Helmet>
        <title>Content Policy & Community Guidelines | LTV VOD</title>
        <meta name="description" content="LTV VOD's Content Policy and Community Guidelines for a safe viewing experience." />
        <link rel="canonical" href="https://ltvproduction.com/content-policy" />
      </Helmet>
      
      <LegalNav />

      <main className="max-w-3xl mx-auto px-5 py-12">
        <h1 className="text-4xl font-bold mb-2">Content Policy & Community Guidelines</h1>
        <p className="text-gray-500 text-sm mb-8">Last updated: March 2026</p>
        
        <p className="text-gray-300 mb-8">At LTV Production, we are committed to providing a safe, respectful, and enjoyable experience for all our viewers. These guidelines outline what content is permitted on our platform and how we expect our community to behave.</p>
        
        <section className="mb-10">
          <h2 className="text-xl font-semibold mb-4 pb-2 border-b border-gray-800">1. Our Content Standards</h2>
          <p className="text-gray-300 mb-4">LTV VOD is a premium video streaming platform featuring professionally produced content including:</p>
          <ul className="list-disc pl-6 text-gray-300 space-y-2">
            <li>Documentaries and educational content</li>
            <li>Live concert recordings and music performances</li>
            <li>Podcasts and talk shows</li>
            <li>Entertainment and lifestyle content</li>
            <li>Original series and exclusive productions</li>
          </ul>
        </section>
        
        <section className="mb-10">
          <h2 className="text-xl font-semibold mb-4 pb-2 border-b border-gray-800">2. Prohibited Content</h2>
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-5">
            <h3 className="text-red-400 font-semibold mb-3">We do not allow content that:</h3>
            <ul className="list-disc pl-6 text-gray-300 space-y-2">
              <li><strong>Promotes violence or harm:</strong> Content that encourages violence, self-harm, or dangerous activities</li>
              <li><strong>Contains hate speech:</strong> Content promoting hatred based on protected characteristics</li>
              <li><strong>Includes illegal material:</strong> Content depicting or promoting illegal activities</li>
              <li><strong>Exploits minors:</strong> Any content that endangers or exploits children</li>
              <li><strong>Violates privacy:</strong> Content that shares personal information without consent</li>
              <li><strong>Infringes copyright:</strong> Content using copyrighted material without authorization</li>
              <li><strong>Contains misleading information:</strong> Deliberately false or deceptive content</li>
            </ul>
          </div>
        </section>
        
        <section className="mb-10">
          <h2 className="text-xl font-semibold mb-4 pb-2 border-b border-gray-800">3. Content Moderation</h2>
          <p className="text-gray-300 mb-4">Our content moderation process includes:</p>
          <ul className="list-disc pl-6 text-gray-300 space-y-2">
            <li><strong>Pre-publication review:</strong> All content is reviewed before publication</li>
            <li><strong>Ongoing monitoring:</strong> We continuously monitor for policy violations</li>
            <li><strong>User reports:</strong> We investigate all user-reported content promptly</li>
            <li><strong>Expert review:</strong> Complex cases are reviewed by senior staff</li>
          </ul>
        </section>
        
        <section className="mb-10">
          <h2 className="text-xl font-semibold mb-4 pb-2 border-b border-gray-800">4. Community Guidelines for Users</h2>
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-5">
            <h3 className="text-green-400 font-semibold mb-3">We encourage our community to:</h3>
            <ul className="list-disc pl-6 text-gray-300 space-y-2">
              <li>Be respectful in comments and interactions</li>
              <li>Provide constructive feedback and reviews</li>
              <li>Report content that violates our policies</li>
              <li>Respect the intellectual property of creators</li>
              <li>Protect their account security</li>
            </ul>
          </div>
        </section>
        
        <section className="mb-10">
          <h2 className="text-xl font-semibold mb-4 pb-2 border-b border-gray-800">5. Reporting Violations</h2>
          <div className="bg-white/5 rounded-lg p-5">
            <h3 className="text-purple-400 font-semibold mb-3">How to Report</h3>
            <ul className="list-disc pl-6 text-gray-300 space-y-2">
              <li><strong>In-app reporting:</strong> Use the report button on videos or comments</li>
              <li><strong>Email:</strong> Send details to <a href="mailto:report@ltvproduction.com" className="text-purple-400">report@ltvproduction.com</a></li>
              <li><strong>Contact form:</strong> Use our <a href="/contact" className="text-purple-400">contact page</a></li>
            </ul>
          </div>
        </section>
        
        <section className="mb-10">
          <h2 className="text-xl font-semibold mb-4 pb-2 border-b border-gray-800">6. Enforcement Actions</h2>
          <p className="text-gray-300 mb-4">Violations may result in:</p>
          <ul className="list-disc pl-6 text-gray-300 space-y-2">
            <li><strong>Warning:</strong> First-time minor violations receive a warning</li>
            <li><strong>Content removal:</strong> Violating content will be removed</li>
            <li><strong>Account suspension:</strong> Serious violations result in suspension</li>
            <li><strong>Permanent ban:</strong> Severe violations lead to permanent termination</li>
          </ul>
        </section>
        
        <section className="mb-10">
          <h2 className="text-xl font-semibold mb-4 pb-2 border-b border-gray-800">7. Contact Us</h2>
          <p className="text-gray-300">For questions about our content policies:</p>
          <p className="text-gray-300"><strong>Email:</strong> <a href="mailto:support@ltvproduction.com" className="text-purple-400">support@ltvproduction.com</a></p>
          <p className="text-gray-300"><strong>Content Team:</strong> <a href="mailto:content@ltvproduction.com" className="text-purple-400">content@ltvproduction.com</a></p>
        </section>
      </main>
      
      {/* Footer */}
      <footer className="bg-[#111] border-t border-gray-800 py-10 mt-10">
        <div className="max-w-7xl mx-auto px-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="font-semibold mb-4">Platform</h3>
              <a href="/vod" className="block text-gray-400 text-sm mb-2 hover:text-purple-400">LTV VOD</a>
              <a href="/vod/browse" className="block text-gray-400 text-sm mb-2 hover:text-purple-400">Browse Videos</a>
              <a href="/live" className="block text-gray-400 text-sm mb-2 hover:text-purple-400">Live Events</a>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Company</h3>
              <a href="/about" className="block text-gray-400 text-sm mb-2 hover:text-purple-400">About Us</a>
              <a href="/contact" className="block text-gray-400 text-sm mb-2 hover:text-purple-400">Contact</a>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Legal</h3>
              <a href="/privacy" className="block text-gray-400 text-sm mb-2 hover:text-purple-400">Privacy Policy</a>
              <a href="/terms" className="block text-gray-400 text-sm mb-2 hover:text-purple-400">Terms of Service</a>
              <a href="/dmca" className="block text-gray-400 text-sm mb-2 hover:text-purple-400">Copyright/DMCA</a>
              <a href="/content-policy" className="block text-gray-400 text-sm mb-2 hover:text-purple-400">Content Policy</a>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Support</h3>
              <a href="mailto:support@ltvproduction.com" className="block text-gray-400 text-sm mb-2 hover:text-purple-400">support@ltvproduction.com</a>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-5 text-gray-500 text-sm">
            © 2026 LTV Production. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ContentPolicy;
